import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Divider,
  Tooltip,
  Image,
  Space,
  DatePicker,
  Button,
} from 'antd';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ORDERS_TYPES } from 'helpers/constants';
import { getOrdersBySlotIdForRouting } from 'store/requests/routing';
import { Loading } from 'components';

const PastOrders = ({
  pastOrders,
  setSelectedOrdersForPast,
  selectedSlot,
  zoneId,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState(pastOrders);
  const [selectedOrderCount, setSelectedOrderCount] = useState(0);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [defaultDate, setDefaultDate] = useState(moment());

  const { t } = useTranslation();

  const [filters, setFilters] = useState({
    date: null,
  });

  const [disabledDates, setDisabledDates] = useState([
    moment(),
    moment().subtract(1, 'days'),
    moment().subtract(2, 'days'),
  ]);

  const disabledDate = (current) => {
    return !disabledDates.some((date) => current.isSame(date, 'day'));
  };

  useEffect(() => {
    const filtered = pastOrders.filter((record) => {
      const recordDate = moment(record.deliveryDate);
      return recordDate.isSame(defaultDate, 'day');
    });

    setSelectedRowKeys([]);
    setSelectedRows([]);
    setFilteredData(filtered);
    setTotalOrderCount(filtered.length);
  }, [pastOrders, defaultDate]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    const filtered = pastOrders?.filter((record) => {
      const recordDate = moment(record.deliveryDate);
      return recordDate.isSame(defaultDate, 'day');
    });

    if (value) {
      const searchValue = value.toLowerCase();
      const filteredAndSearched = filtered?.filter((record) =>
        Object.keys(record).some((key) =>
          record[key]?.toString()?.toLowerCase()?.includes(searchValue),
        ),
      );
      setFilteredData(filteredAndSearched);
      setTotalOrderCount(filteredAndSearched.length);
    } else {
      setFilteredData(filtered);
      setTotalOrderCount(filtered.length);
    }
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedOrderCount(selectedRowKeys.length);
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
    setSelectedOrdersForPast(selectedRows);
  };

  const rowSelection = {
    selectedRows,
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleDatePickerChange = (date, dateString) => {
    setDefaultDate(date);
  };

  const reload = () => {
    setLoading(true);

    if (selectedSlot.length > 0) {
      Promise.all([
        getOrdersBySlotIdForRouting({
          slotIdList: [...selectedSlot],
          previousDayNotRoutedOrdersIncluded: false,
          zoneId,
        }),
        getOrdersBySlotIdForRouting({
          slotIdList: [...selectedSlot],
          previousDayNotRoutedOrdersIncluded: true,
          zoneId,
        }),
      ])
        .then(([currentOrders, allOrders]) => {
          const curOrders = currentOrders;

          const allPastOrders = allOrders.filter((order) => {
            return !curOrders.some(
              (currentOrder) => currentOrder.orderNumber === order.orderNumber,
            );
          });

          const filtered = allPastOrders.filter((record) => {
            const recordDate = moment(record.deliveryDate);
            return recordDate.isSame(defaultDate, 'day');
          });
          setFilteredData(filtered);
        })
        .catch((error) => {
          console.error('Hata:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const columns = [
    {
      title: t('selectAll'),
      dataIndex: 'customerOrderNumber',
      key: 'customerOrderNumber',
      width: '70px',
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '',
      dataIndex: 'addressText',
      key: 'address',
      width: '120px',
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: defaultDate
        ? `${t('date')} (${defaultDate?.format('DD/MM/YYYY')})`
        : t('date'),
      dataIndex: 'icons',
      key: 'date',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            defaultPickerValue={defaultDate}
            value={defaultDate}
            onChange={handleDatePickerChange}
            disabledDate={disabledDate}
          />
        </div>
      ),
      filterSearch: true,
      onFilter: (value, record) => {
        const selectedDate = moment(value);
        const recordDate = moment(record.deliveryDate);
        return recordDate.isSame(selectedDate, 'day');
      },

      render: (text, record) => (
        <Space>
          {record.containsFrozenProduct && (
            <Tooltip title={t('containsFrozen')}>
              <Image
                src="/static/img/frozenProduct.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.customerType === 'VIP' && (
            <Tooltip title={t('eliteCustomer')}>
              <Image
                src="/static/img/eliteCustomer.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'COLLECTED' && (
            <Tooltip title={t('collected')}>
              <Image
                src="/static/img/collected.svg"
                width={25}
                height={25}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'COLLECTING' && (
            <Tooltip title={t('collecting')}>
              <Image
                src="/static/img/gathering.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'NEW_ORDER' && (
            <Tooltip title={t('notCollected')}>
              <Image
                src="/static/img/notCollected.svg"
                width={25}
                height={25}
                preview={false}
              />
            </Tooltip>
          )}
          {record.partner === 'SOK' && (
            <Tooltip title="Sipariş Platformu: (CepteŞok)">
              <Image
                src="/static/img/ceptesoklogo.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.partner === 'ISTEGELSIN' && (
            <Tooltip title="Sipariş Platformu: (İsteGelsin)">
              <Image
                src="/static/img/istegelsinlogo.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderType === ORDERS_TYPES.RETURN && (
            <Tooltip title={t('returnOrder')}>
              <Image
                src="/static/img/returnOrder.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {loading && (
        <div className="loadingDiv">
          <Loading />
        </div>
      )}
      <Divider style={{ margin: '12px 0px' }} orientation="left" plain>
        Geçmiş Siparişler
      </Divider>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        <Input
          placeholder={t('orderSearch')}
          onChange={handleSearch}
          style={{ width: '30%', borderRadius: '5px' }}
          suffix={<SearchOutlined />}
        />
        <span style={{ fontSize: '12px', marginLeft: '5px' }}>
          {t('selectedOrderCount')} : {selectedOrderCount}
        </span>
        <span style={{ fontSize: '12px', margin: '2px' }}>
          {t('totalOrderCountForRouting')} : {totalOrderCount}
        </span>
        <ReloadOutlined
          style={{ cursor: 'pointer' }}
          color="green"
          onClick={reload}
        />
      </div>
      <Divider style={{ margin: '12px 0px' }} />
      {pastOrders && (
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          showHeader={true}
          rowSelection={rowSelection}
          rowKey="id"
          size="small"
          loading={tableLoading}
          onChange={(pagination, filters) => {
            setFilters(filters);
          }}
          scroll={{ y: '19vh' }}
        />
      )}
    </div>
  );
};

export default PastOrders;
